import { FrontendConstants } from '../common/FrontendConstants';
import { HttpError } from '../common/HttpError';
import { Settings } from '../common/Settings';
import { AuthProvider } from '../providers/AuthProvider';
import type { IAuthOptions } from '../contracts/IAuthOptions';
import type { IAuthProvider } from '../contracts/IAuthProvider';

/** File-local constants for AuthClient. */
const localConstants = {
  errNotAuthenticated: 'Not authenticated'
} as const;

/** Client for authentication operations including token management. */
export class AuthClient {
  /** Singleton instance. */
  private static instance: AuthClient;
  /** Cached auth adapter to avoid creating a new Firebase app on every call. */
  private cachedAdapter: IAuthProvider | null = null;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton AuthClient instance. */
  public static GetInstance(): AuthClient {
    return AuthClient.instance ?? (AuthClient.instance = new AuthClient());
  }

  /**
   * Return Authorization header for API calls. Obtains a token silently from the local Firebase Auth session (same-origin in production; the auth adapter handles refresh).
   *
   * @param adapter - IAuthProvider for advanced auth operations.
   * @returns Promise resolving to an Authorization header object.
   */
  private async getAuthHeaders(adapter: IAuthProvider): Promise<Record<string, string>> {
    const token: string | undefined = await adapter.GetTokenSilently();
    return token ? { [FrontendConstants.HeaderAuthorization]: `${FrontendConstants.BearerScheme}${token}` } : {};
  }

  /**
   * Fetch wrapper that injects Authorization header when an auth adapter is provided.
   *
   * @param uriBase - The common base URI for both the service and its auth endpoint (e.g., SERVICE_LOBBY_URI).
   * @param route - The route to append to the base URI for the API call (e.g., 'sessions').
   * @param options - Fetch init options.
   * @param adapter - Optional IAuthClientAdapter for advanced auth operations.
   * @returns Promise resolving to the Response object.
   */
  public async Fetch(uriBase: string, route: string, options: globalThis.RequestInit, adapter?: IAuthProvider): Promise<Response> {
    let headers: Record<string, string> = {};
    if (adapter) {
      headers = await this.getAuthHeaders(adapter);
      // A provided adapter marks an auth-required endpoint (gated/admin/dashboard/tenant). When no token is available the local Firebase session is gone (for example, signed out in another
      // same-origin app), so the request can only fail at the gateway with a missing Authorization header. Fail fast with a typed 401 so callers can log the user out cleanly instead of
      // emitting gateway errors.
      if (!headers[FrontendConstants.HeaderAuthorization]) {
        throw new HttpError(FrontendConstants.HTTPStatusUnauthorized, localConstants.errNotAuthenticated);
      }
    }
    const response: Response = await fetch(`${uriBase}/${route}`, { ...options, headers: { ...(options.headers || {}), ...headers } });
    return response;
  }

  /** @returns Promise resolving to the FirebaseAuthProvider instance (cached after first call). */
  public async GetAuthClientAdapter(): Promise<IAuthProvider> {
    if (this.cachedAdapter) return this.cachedAdapter;
    const authOptions: IAuthOptions = {
      apiKey: Settings.AUTH_API_KEY,
      authDomain: Settings.AUTH_DOMAIN,
      projectId: Settings.AUTH_PROJECT_ID,
      appId: Settings.AUTH_APP_ID
    };
    const auth = new AuthProvider(authOptions);
    await auth.Init();
    this.cachedAdapter = auth;
    return auth;
  }
}
